import React, { useState } from "react";
import { Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import Request from "../../../../helpers/request";
import * as url from "../../../../helpers/url_helper";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import LogsDataTable from "./LogsDataTable";
import ScheduleFormOffcanvas, { ScheduleRow } from "./ScheduleModel";

export interface ChipsControlProps {
  userId: any;
  selectedPremise?: any;
}

const handleUnauthorized = () => {
  localStorage.removeItem("userInfo");
  localStorage.removeItem("selectedPremiseID");
  localStorage.removeItem("selectedUserID");
  localStorage.removeItem("selectedUserEmail");
  localStorage.removeItem("Authorization");
  localStorage.removeItem("permissions");
  window.location.reload();
};

const extractErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.msg?.message ||
  error?.response?.data?.msg ||
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  fallback;

const ScheduleList = ({ userId, selectedPremise }: ChipsControlProps) => {
  const [loading, setLoading] = useState<boolean>(false);

  // Controls the slide-in Create/Edit panel
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingSchedule, setEditingSchedule] = useState<ScheduleRow | null>(
    null,
  );

  // Bumping this forces LogsDataTable to remount and refetch after create/update/delete.
  // If LogsDataTable exposes its own refresh prop/ref, swap this out for that instead.
  const [refreshKey, setRefreshKey] = useState<number>(0);

  // Tracks which row's delete button is mid-request, so only that row shows a spinner
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Delete confirmation modal state
  const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<ScheduleRow | null>(null);

  const openCreate = () => {
    setEditingSchedule(null);
    setShowForm(true);
  };

  const openEdit = (row: ScheduleRow) => {
    setEditingSchedule(row);
    setShowForm(true);
  };

  const handleSaved = () => {
    setRefreshKey((k) => k + 1);
  };

  const openDeleteConfirm = (row: ScheduleRow) => {
    setDeleteTarget(row);
    setDeleteModalOpen(true);
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const scheduleId = deleteTarget.scheduleId || deleteTarget._id;
    if (!scheduleId) return;

    setDeletingId(scheduleId);
    try {
      await Request.delete(`${url.BASE_URL}${url.DELETE_USER_SCHEDULE}`, {
        scheduleId,
      });
      // console.log("delete target ", deleteTarget)
      toast.success("Schedule deleted");
      handleSaved();
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    } catch (error: any) {
      console.error("Error deleting schedule:", error);
      toast.error(extractErrorMessage(error, "Failed to delete schedule"));
      if (error?.response?.status === 401) handleUnauthorized();
    } finally {
      setDeletingId(null);
    }
  };
  const formatNextRun = (value?: string) => {
    if (!value) return "-";

    const [date, ...timeParts] = value.split(" ");
    const time = timeParts.join(" ");
    const [year, month, day] = date.split("-");
    const formattedDate = `${day}-${month}-${year}`;

    return `${formattedDate} / ${time}`;
  };
  const columns = [
    // {
    //   name: "Slave",
    //   selector: (row: any) => row?.slave || "-",
    //   sortable: true,
    //   grow: 1,
    //   wrap: true,
    // },
    {
      name: "Slave & scene",
      selector: (row: any) => {
        if (row?.slave) {
          return row.slave;
        }

        if (row?.deviceType === "scene") {
          return row?.deviceName || "-";
        }

        return "-";
      },
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: "Type",
      selector: (row: any) => row?.deviceType || "-",

      sortable: true,
      grow: 1,
      wrap: true,
    },

    {
      name: "Button",
      selector: (row: any) => row?.button || "-",
      sortable: true,
      grow: 0.5,
      wrap: true,
    },
    {
      name: "Button Value",
      selector: (row: any) => row?.executeValue || "-",
      sortable: true,
      grow: 1,
      wrap: true,
    },
    {
      name: "Day Numbers",
      cell: (row: any) => {
        const dayMap: any = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

        const days = row?.dayNumbers?.map((i: number) => dayMap[i]) || [];

        return days.length ? (
          <div className="d-flex flex-wrap gap-1">
            {days.map((d: string, idx: number) => (
              <span
                key={idx}
                className="badge bg-primary text-white"
                style={{ fontSize: "11px" }}
              >
                {d}
              </span>
            ))}
          </div>
        ) : (
          "-"
        );
      },
      sortable: false,
      grow: 2,
      wrap: true,
    },
    {
      name: "Is Active",
      selector: (row: any) =>
        row?.isActive ? (
          <div className="bg-success text-white px-2 py-1 rounded text-center fw-bold">
            Active
          </div>
        ) : (
          <div className="bg-danger text-white px-2 py-1 rounded text-center fw-bold">
            Inactive
          </div>
        ),
      sortable: true,
      grow: 1,
      wrap: true,
    },
    {
      name: "Time",
      cell: (row: any) => <span>{row?.istTime ? row.istTime : "-"}</span>,
      sortable: true,
      grow: 0.5,
      wrap: true,
    },
    {
      name: "Is Repeat",
      selector: (row: any) => (row?.isRepeat ? "True" : "False"),
      sortable: true,
      grow: 1,
      wrap: true,
    },
    {
      name: "Next Run",
      cell: (row: any) =>
        row?.istNextRun ? <span>{formatNextRun(row.istNextRun)}</span> : "-",
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: "User Type",
      selector: (row: any) => (row?.createdByFlag ? row.createdByFlag : "-"),
      cell: (row: any) =>
        row.createdByFlag === "ADMIN" ? (
          <span className="font-size-11 badge-soft-primary badge bagde bg-secondary">
            Admin
          </span>
        ) : row.createdByFlag === "MAIN" ? (
          <span className="font-size-11 badge-soft-success badge bg-secondary">
            Main
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-secondary">
            Sub
          </span>
        ),
      sortable: true,
    },
    {
      name: "Created By",
      selector: (row: any) => (row?.createdByName ? row.createdByName : "-"),
      cell: (row: any) =>
        row.createdByUserId && row.createdByName ? (
          <span className="">
            <a
              href={`/users/profile/${row.createdByUserId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.createdByName}
            </a>
          </span>
        ) : (
          <span className="">{row?.createdByName}</span>
        ),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row: any) => {
        const scheduleId = row.scheduleId || row._id;
        return (
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-link p-0 text-dark"
              title="Edit"
              onClick={() => openEdit(row)}
            >
              <i className="bx bx-pencil" style={{ fontSize: 18 }} />
            </button>
            <button
              type="button"
              className="btn btn-link p-0 text-danger"
              title="Delete"
              onClick={() => openDeleteConfirm(row)}
              disabled={deletingId === scheduleId}
            >
              {deletingId === scheduleId ? (
                <Spinner size="sm" />
              ) : (
                <i className="bx bx-trash" style={{ fontSize: 18 }} />
              )}
            </button>
          </div>
        );
      },
      sortable: false,
      grow: 0.7,
      wrap: true,
    },
  ];

  return (
    <div>
      {isDeleteModalOpen && (
        <ConfirmationModal
          headerTitle="Delete Schedule?"
          text={`Are you sure you want to delete the schedule for "${
            deleteTarget?.slave || deleteTarget?.deviceName || "this switch"
          }"? This cannot be undone.`}
          buttonText="Delete"
          onClick={confirmDelete}
          cancleBtnClick={() => {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }}
          loading={
            deletingId === (deleteTarget?.scheduleId || deleteTarget?._id)
          }
        />
      )}

      <LogsDataTable
        key={refreshKey}
        apiURL={`${url.BASE_URL}${url.GET_SCHEDULE_LIST}`}
        title="Schedule List"
        columns={columns}
        loading={loading}
        setLoading={setLoading}
        userId={userId}
        selectedPremise={selectedPremise}
        actionButton={
          <Button
            color="dark"
            size="sm"
            className="d-flex align-items-center gap-1 p-1 fw-bold rounded-3"
            onClick={openCreate}
          >
            <i className="bx bx-plus" style={{ fontSize: 16 }} />
            Create Schedule
          </Button>
        }
      />

      <ScheduleFormOffcanvas
        show={showForm}
        onClose={() => setShowForm(false)}
        onSaved={handleSaved}
        userId={userId}
        selectedPremise={selectedPremise}
        editingSchedule={editingSchedule}
      />
    </div>
  );
};

export default ScheduleList;
